import { useState, useEffect } from "react"
import Button from "../../../ui/Button"
import { Disclosure, Transition } from "@headlessui/react"
import classNames from "../../../../utils/classNames"
import { ChevronRightIcon } from "@heroicons/react/24/solid"
import SelectInput, { Option } from "../../../ui/SelectInput"
import { SideNavTabOption } from "~/components/Admin/Settings/pages/pricing/types"
import formatCampingStyleName from "~/utils/formatCampingStyleName"
import { ROUTER_PATHS } from "~/routes/constants"
import { useCampingStyles } from "~/api2/hooks/useCampingStyles"
import { useMe } from "~/api2/hooks/useMe"
import { useNavigate } from "react-router-dom"

const tabOptions: SideNavTabOption[] = [
  { label: "Camp info", value: "camp" },
  {
    label: "Pricing",
    value: "pricing",
    subOptions: [{ label: "General pricing", value: "general" }],
  },
  { label: "Products", value: "products", route: ROUTER_PATHS.ADMIN_SETTINGS_PRODUCTS },
  { label: "Invoices", value: "invoices", route: ROUTER_PATHS.ADMIN_SETTINGS_INVOICES },
  { label: "Coupons", value: "coupons", route: ROUTER_PATHS.ADMIN_SETTINGS_COUPONS },
  { label: "Automated Messages", value: "automated-messages", route: ROUTER_PATHS.ADMIN_SETTINGS_AUTOMATED_MESSAGES },
  { label: "Rules & terms", value: "rules-terms", route: ROUTER_PATHS.ADMIN_SETTINGS_RULES_TERMS },
  { label: "Users", value: "users" },
]

interface SettingsNavBarProps {
  onTabChange: (tab: string) => void
}

const SettingsNavBar = ({ onTabChange }: SettingsNavBarProps) => {
  const navigate = useNavigate()
  const { data: user } = useMe()
  const {
    index: { data: campingStyles },
  } = useCampingStyles()
  const isParkAdminUser = user?.email?.includes("@poweredbypark.com") || false
  const [selectedTab, setSelectedTab] = useState("camp")

  const migratedTabs = ["products", "coupons", "automated-messages", "invoices", "rules-terms"]

  useEffect(() => {
    const toTab = localStorage.getItem("toTab")
    if (window.location.pathname === "/admin/reservations" && migratedTabs.includes(toTab)) return
    if (toTab) {
      setSelectedTab(toTab)
    }
    return () => {
      localStorage.removeItem("toTab")
    }
  }, [])

  useEffect(() => {
    const path = window.location.pathname
    if (path.includes("settings/")) {
      const parts = path.split("/")
      const settingsIndex = parts.indexOf("settings")
      if (settingsIndex !== -1 && parts.length > settingsIndex + 1) {
        const nextPart = parts[settingsIndex + 1]
        setSelectedTab(nextPart)
      }
    }
  }, [])

  useEffect(() => {
    if (
      !tabOptions.some((tab) => tab.value === selectedTab) &&
      !campingStyles?.some((campStyle) => campStyle.id === selectedTab)
    ) {
      onTabChange(selectedTab)
    }
  }, [selectedTab, onTabChange, campingStyles])

  useEffect(() => {
    // Always call onTabChange when selectedTab changes
    onTabChange(selectedTab)
  }, [selectedTab, onTabChange, campingStyles])

  const setSelectedOption = (option: Option | Option[]) => {
    if (!Array.isArray(option)) {
      localStorage.setItem("from", window.location.pathname)
      localStorage.setItem("fromTab", selectedTab)
      localStorage.setItem("toTab", option.value)
      localStorage.setItem("toNav", "settings")
      if (option.route) {
        if (window.location.pathname !== "/admin/reservations") {
          navigate(option.route)
          setSelectedTab(option.value)
        } else {
          window.location.href = option.route
        }
      } else if (window.location.pathname !== "/admin/reservations") {
        window.location.href = "/admin/reservations"
      } else {
        setSelectedTab(option.value)
        onTabChange(option.value)
      }
    }
  }

  return (
    <div className="xs:flex md:block py-4 xs:pl-4 lg:px-0">
      <div className="flex justify-between w-full">
        <h2 className="font-bold text-2xl mb-2.5">Settings</h2>
        <div className="sm:block md:hidden ml-3 w-56">
          <SelectInput
            options={tabOptions}
            selected={tabOptions.find((option: Option) => option.value === selectedTab) || { label: "", value: "" }}
            setSelected={setSelectedOption}
            large
          />
        </div>
      </div>
      <div className="w-48 mt-12 hidden md:block">
        {tabOptions.map((tab: SideNavTabOption) => (
          <div key={tab.value}>
            {tab.subOptions && campingStyles ? (
              <Disclosure key={`disclosure-${selectedTab}`} defaultOpen={selectedTab.startsWith("pricing")}>
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      className="w-full text-left py-2.5 px-2 flex items-center justify-between"
                      onClick={() => {
                        if (selectedTab !== tab.value && !tab.subOptions) {
                          setSelectedTab(tab.value)
                          onTabChange(tab.value)
                        }
                      }}
                    >
                      {formatCampingStyleName(tab.label)}
                      <span
                        className={`ml-2 transform transition-transform ease-in-out duration-300 ${
                          open ? "rotate-90" : "rotate-0"
                        }`}
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </span>
                    </Disclosure.Button>
                    <Transition
                      show={open}
                      enter="transition-opacity duration-300"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition-opacity duration-300"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Disclosure.Panel static>
                        <div className="ml-2 mt-2 space-y-2">
                          {[
                            ...(tab.subOptions || []),
                            ...((isParkAdminUser &&
                              campingStyles?.map(({ id, name }) => ({
                                label: name,
                                value: id,
                              }))) ||
                              []),
                          ].map((tabOption) => (
                            <Button
                              key={tabOption.value}
                              onClick={() => {
                                localStorage.setItem("from", window.location.pathname)
                                localStorage.setItem("fromTab", selectedTab)
                                localStorage.setItem("toTab", `pricing_${tabOption.value}`)
                                localStorage.setItem("toNav", "settings")
                                if (window.location.pathname !== "/admin/reservations") {
                                  window.location.href = "/admin/reservations"
                                } else {
                                  setSelectedTab(`pricing_${tabOption.value}`)
                                  onTabChange(`pricing_${tabOption.value}`)
                                }
                              }}
                              variant="link"
                              className={classNames(
                                "w-full text-left px-2 py-2.5",
                                selectedTab === `pricing_${tabOption.value}` &&
                                  "bg-green-100 text-green-700 font-semibold hover:no-underline cursor-default",
                              )}
                            >
                              {formatCampingStyleName(tabOption.label)}
                            </Button>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            ) : (
              <Button
                variant="link"
                className={classNames(
                  "w-full text-left px-2 py-2.5",
                  selectedTab === tab.value &&
                    "bg-green-100 text-green-700 font-semibold hover:no-underline cursor-default",
                )}
                size="no-size"
                onClick={() => {
                  localStorage.setItem("from", window.location.pathname)
                  localStorage.setItem("fromTab", selectedTab)
                  localStorage.setItem("toTab", tab.value)
                  localStorage.setItem("toNav", "settings")
                  if (tab.route) {
                    if (window.location.pathname !== "/admin/reservations") {
                      navigate(tab.route)
                      setSelectedTab(tab.value)
                    } else {
                      window.location.href = tab.route
                    }
                  } else if (window.location.pathname !== "/admin/reservations") {
                    window.location.href = "/admin/reservations"
                  } else {
                    setSelectedTab(tab.value)
                    onTabChange(tab.value)
                  }
                }}
              >
                {tab.label}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default SettingsNavBar
